import type { IAppContext } from 'App/AppContext';
import { globalAppName } from 'globalConstants';
import type { TFunction } from 'i18next';
import { DBProcesLoggerLogLevel, LogDTO } from 'dto/system/logDTO';
import { apiAddProcessLog } from 'services/Api/userService';
import { apiRequest } from 'services/Auth/authConfig';
import { AuthErrorCodes } from 'services/Auth/authErrors';
import Config from 'services/Config/configService';
import logger, { getLogWithDateStamp, globalLog } from 'services/Logging/logService';
import AppError, { normalizeError } from 'utils/appError';
import ControllerException from 'utils/controllerException';
import { safeStringify } from 'utils/stringify';
import {
  getIdentityProviderErrorCode,
  isAbortOrCancelError,
  isAuthReloadError,
  isLoginError,
  isNetworkError,
  isTenantRestoreFailedError,
  isTenantRestoreInProgressError,
  isTenantSuspendedError,
} from './ErrorMessageHelper';

export const getErrorMessageText = (error: AppError, t: TFunction<string[]>): string => {
  //a refusal of the identity layer arrives as a 401 with an IDP code; its own text says what to do
  const identityProviderCode = getIdentityProviderErrorCode(error);
  if (identityProviderCode) {
    return ControllerException.getMessage(identityProviderCode, t);
  }

  //the errors the app's own identity layer raises
  switch (error.code) {
    case AuthErrorCodes.MicrosoftIdentityRequired:
      return t('identityproviders:Errors.MicrosoftIdentityRequired');
    case AuthErrorCodes.OidcAdminPolicy:
      return t('identityproviders:Errors.AdminPolicy', { appName: globalAppName });
    case AuthErrorCodes.OidcSignInAgain:
      return t('identityproviders:Errors.SignInAgain');
    case AuthErrorCodes.OidcIssuerUnknown:
      return t('identityproviders:Errors.IssuerUnknown');
  }

  //the three lock states of an organization arrive as a 401 whose body names the state; without these the user
  //reads "access denied" for an organization that is being restored, failed to restore, or is suspended
  if (isTenantRestoreInProgressError(error)) {
    return t('translation:Errors.RestoreInProgress.Message');
  }

  if (isTenantRestoreFailedError(error)) {
    return t('translation:Errors.RestoreFailed.Message');
  }

  if (isTenantSuspendedError(error)) {
    return t('translation:Errors.TenantSuspended.Message');
  }

  if (isNetworkError(error) || isAbortOrCancelError(error) || isLoginError(error)) {
    return t('translation:Errors.NetworkError.Message');
  }

  switch (error.code) {
    case '429':
      return t('translation:Errors.TooManyRequests.Message');
    case '404':
      return t('translation:Errors.DatabaseNotFound.Message');
    case '409':
      // A typed conflict (the API attached a ControllerException code) has its own translated message
      if (AppError.isControllerException(error)) {
        return AppError.getControllerExceptionMsg(error, t) ?? error.message;
      }

      return t('translation:Errors.DatabaseConflict.Message');
    case '401':
    case '403':
      return t('translation:Errors.AccessDenied.Message');
    case '400':
    case '402':
    case '500':
      if (error.debug && error.debug.indexOf('AccessDenied') >= 0) {
        return t('translation:Errors.AccessDenied.Message');
      }

      if (AppError.isControllerException(error)) {
        return AppError.getControllerExceptionMsg(error, t) ?? error.message;
      }

      return t('translation:Errors.ApiError.Message');
    case '999':
      return t('translation:Errors.Unhandled.Message');
    default:
      return error.message;
  }
};

//
// Sends the client log of an error to the API (POST users/processlog), where an error-level entry becomes a
// support ticket. Best effort: an API that is unreachable, or does not have the endpoint, is logged quietly.
//
export const saveErrorToApi = async (
  error: AppError | undefined,
  appContext: IAppContext,
  t: TFunction<string[]>,
): Promise<boolean> => {
  try {
    if (!error) {
      return false;
    }

    // A connectivity failure means the API is unreachable by definition, and a locked organization refuses the
    // upload too: uploading would only add noise.
    if (
      isNetworkError(error) ||
      isTenantSuspendedError(error) ||
      isTenantRestoreInProgressError(error) ||
      isTenantRestoreFailedError(error)
    ) {
      return false;
    }

    // Without a signed-in user there is no token to upload with.
    if (!appContext.isAuthenticated) {
      return false;
    }

    const logDTO = new LogDTO(
      error.fromApi === true
        ? `Global client log for API exception ${error.apiLogId || 0}`
        : (error.message ?? 'Unknown error'),
    );

    logDTO.error = safeStringify(error);
    logDTO.add(getLogWithDateStamp('Environment info: ' + Config.getEnvInfo()));
    logDTO.add(getLogWithDateStamp(error.stack));
    logDTO.add(getLogWithDateStamp(error.debug));
    logDTO.add('Global log entries');
    globalLog.messages.forEach((message) => logDTO.add(message));

    const filterMsg: string[] = [
      'access_denied',
      'interaction_in_progress',
      'user_cancelled',
      'consent_required',
      'popup_window_error',
      'hash_empty_error',
      'InvalidAuthenticationToken',
      'AADSTS7000229',
      'AADSTS500011',
      'AADSTS50173',
      'AADSTS650051',
      'AADSTS70008',
    ];

    const filterBackEndStatusCodes: string[] = ['403', '404', '409'];
    const isRateLimited = error.code === '429';

    let shouldNotCreateTicket =
      !isRateLimited &&
      (error.fromApi === true ||
        isAuthReloadError(error) ||
        isAbortOrCancelError(error) ||
        (error.code ? filterBackEndStatusCodes.includes(error.code) : false));

    if (shouldNotCreateTicket === false) {
      // Entries are matched case-insensitively against error.message and error.debug
      shouldNotCreateTicket = filterMsg.some((message) => {
        if (error.code === message) {
          return true;
        }

        const needle = message.toLowerCase();

        if (error.message && error.message.toLowerCase().indexOf(needle) >= 0) {
          return true;
        }

        if (error.debug && error.debug.toLowerCase().indexOf(needle) >= 0) {
          return true;
        }

        return false;
      });
    }

    if (isRateLimited) {
      logDTO.name = 'Global client log for API exception - Rate limit exceeded';
      logDTO.level = DBProcesLoggerLogLevel.Warning;
    } else if (shouldNotCreateTicket) {
      logDTO.level = DBProcesLoggerLogLevel.Info;
    } else {
      logDTO.level = DBProcesLoggerLogLevel.Error;
    }

    const accessToken = await appContext.getAccessToken(apiRequest.scopes);
    await apiAddProcessLog(logDTO, accessToken);
    globalLog.clear();

    return true;
  } catch (err) {
    // An upload that fails because the API is unreachable, or does not offer the endpoint, is nothing the user
    // can act on: log quietly and stop.
    const uploadError = normalizeError(err);
    // A restore that locked the organization between the error and this upload refuses it the same way.
    if (
      isNetworkError(uploadError) ||
      isTenantRestoreInProgressError(uploadError) ||
      isTenantRestoreFailedError(uploadError) ||
      isTenantSuspendedError(uploadError) ||
      uploadError?.code === '404' ||
      uploadError?.code === '405'
    ) {
      logger.debug('Skipped uploading client log to API: endpoint not available');

      return false;
    }

    logger.error(new AppError('Error while uploading log to API. ' + safeStringify(err)));
    appContext.showNotification(t('translation:Errors.UploadClientLog.Message'), true);

    return false;
  }
};
